import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader, StatTile, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Icon } from "../app/Icon";
import { initials } from "../profiles/ProfileMenu";
import { useSaved } from "../saved/SavedContext";
import { OnboardingChecklist, type OnboardingStep } from "../components/OnboardingChecklist";
import { PROFILE_TYPE_LABELS } from "../lib/profiles";
import { listJobs, myJobs, myApplications, payLabel, HIRING_TYPES } from "../lib/jobs";
import { listLoads, myLoads, myBids, laneLabel, LOAD_POST_TYPES, LOAD_BID_TYPES } from "../lib/loads";
import { listConversations } from "../lib/messaging";

type Jobs = Awaited<ReturnType<typeof listJobs>>["jobs"];
type Loads = Awaited<ReturnType<typeof listLoads>>["loads"];
type Postings = Awaited<ReturnType<typeof myJobs>>["jobs"];
type MyLoadList = Awaited<ReturnType<typeof myLoads>>["loads"];
type Apps = Awaited<ReturnType<typeof myApplications>>["applications"];
type Bids = Awaited<ReturnType<typeof myBids>>["bids"];
type Convos = Awaited<ReturnType<typeof listConversations>>["conversations"];

function ago(at?: string): string {
  if (!at) return "";
  const s = Math.max(0, (Date.now() - +new Date(at)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Row({ to, tile, title, sub, right }: { to: string; tile: string; title: string; sub?: string; right?: ReactNode }) {
  return (
    <Link to={to} className="flex items-center gap-3 border-t border-line py-3 first:border-t-0 first:pt-0 hover:opacity-80">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-[11px] font-black text-accent-strong">{tile}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">{title}</span>
        {sub && <span className="block truncate font-mono text-xs text-muted">{sub}</span>}
      </span>
      {right && <span className="shrink-0 text-right">{right}</span>}
    </Link>
  );
}

function ViewAll({ to }: { to: string }) {
  return <Link to={to} className="font-mono text-xs font-bold text-accent-strong">View all →</Link>;
}

export function Home() {
  const { account } = useAuth();
  const { profiles, active, loading } = useProfiles();
  const { savedIds } = useSaved();
  const [obDismissed, setObDismissed] = useState(() => localStorage.getItem("rt.onboarding.dismissed") === "1");
  function dismissOnboarding() {
    localStorage.setItem("rt.onboarding.dismissed", "1");
    setObDismissed(true);
  }

  const type = active?.type;
  const isHiring = type ? HIRING_TYPES.includes(type) : false;
  const isPoster = type ? LOAD_POST_TYPES.includes(type) : false;
  const isBidder = type ? LOAD_BID_TYPES.includes(type) : false;
  const isDriver = type === "driver";

  const [jobs, setJobs] = useState<Jobs>([]);
  const [loads, setLoads] = useState<Loads>([]);
  const [postings, setPostings] = useState<Postings>([]);
  const [myLoadList, setMyLoadList] = useState<MyLoadList>([]);
  const [apps, setApps] = useState<Apps>([]);
  const [bids, setBids] = useState<Bids>([]);
  const [convos, setConvos] = useState<Convos>([]);

  useEffect(() => {
    let cancelled = false;
    async function safe<T>(fn: () => Promise<T>): Promise<T | null> {
      try { return await fn(); } catch { return null; }
    }
    async function run() {
      if (!active) return;
      const [j, l, c] = await Promise.all([safe(listJobs), safe(listLoads), safe(listConversations)]);
      if (cancelled) return;
      if (j) setJobs(j.jobs);
      if (l) setLoads(l.loads);
      if (c) setConvos(c.conversations);
      if (isHiring) { const r = await safe(() => myJobs(active.id)); if (r && !cancelled) setPostings(r.jobs); }
      if (isPoster) { const r = await safe(() => myLoads(active.id)); if (r && !cancelled) setMyLoadList(r.loads); }
      if (isDriver) { const r = await safe(() => myApplications(active.id)); if (r && !cancelled) setApps(r.applications); }
      if (isBidder) { const r = await safe(() => myBids(active.id)); if (r && !cancelled) setBids(r.bids); }
    }
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  // Onboarding — no profile yet. (e2e depends on this exact button.)
  if (!loading && profiles.length === 0) {
    return (
      <div className="mx-auto grid max-w-md content-center gap-4 py-10 text-center">
        <h1 className="font-display text-2xl font-black tracking-tight">Welcome to RecruitTrucker</h1>
        {account?.email && <p className="font-mono text-sm text-muted">Signed in as {account.email}</p>}
        <p className="text-muted">
          Create your first profile to get on the boards — a driver profile to find jobs, a company profile to hire and
          move freight, and more.
        </p>
        <div className="justify-self-center">
          <Link to="/profiles/new"><Button>Create your first profile</Button></Link>
        </div>
      </div>
    );
  }

  const name = active?.display_name?.split(/\s+/)[0] ?? account?.email?.split("@")[0] ?? "there";
  const verified = Boolean(account?.identity_verified);
  const savedJobIds = new Set(savedIds("jobs"));
  const savedJobs = jobs.filter((j) => savedJobIds.has(j.id));

  // Primary "recommended" + "activity" sections, adapted to the active profile.
  const recommended = isDriver || (!isHiring && !isPoster)
    ? { title: "Latest jobs", viewAll: "/jobs", rows: jobs.slice(0, 3).map((j) => (
        <Row key={j.id} to={`/jobs/${j.id}`} tile={initials(j.poster_name ?? j.title)} title={j.title} sub={j.location ?? undefined}
          right={<><span className="block font-mono text-sm font-bold text-verified">{payLabel(j.pay_min, j.pay_max, j.pay_type) ?? "—"}</span><span className="font-mono text-[10px] text-verified">VERIFIED ✓</span></>} />
      )) }
    : isHiring
    ? { title: "Your postings", viewAll: "/jobs/mine", rows: postings.slice(0, 3).map((p) => (
        <Row key={p.id} to={`/jobs/${p.id}`} tile={initials(p.title)} title={p.title} sub={`${p.application_count} applicant${p.application_count === 1 ? "" : "s"}`} right={<StatusLabel status={p.status} />} />
      )) }
    : { title: "Your loads", viewAll: "/loads/mine", rows: myLoadList.slice(0, 3).map((l) => (
        <Row key={l.id} to={`/loads/${l.id}`} tile={initials(l.origin)} title={laneLabel(l.origin, l.destination)} sub={`${l.bid_count} bid${l.bid_count === 1 ? "" : "s"}`} right={<StatusLabel status={l.status} />} />
      )) };

  const activity = isDriver
    ? { title: "My applications", viewAll: "/applications", rows: apps.slice(0, 3).map((a) => (
        <Row key={a.id} to="/applications" tile={initials(a.poster_name ?? a.title ?? "?")} title={a.title ?? "—"} sub={a.poster_name ?? undefined} right={<StatusLabel status={a.status} />} />
      )) }
    : isBidder && !isPoster
    ? { title: "My bids", viewAll: "/bids", rows: bids.slice(0, 3).map((b) => (
        <Row key={b.id} to="/bids" tile={initials(b.origin ?? "?")} title={laneLabel(b.origin ?? "—", b.destination ?? "—")} sub={`$${b.amount.toLocaleString()}`} right={<StatusLabel status={b.status} />} />
      )) }
    : isPoster
    ? { title: "Recent loads", viewAll: "/loads/mine", rows: myLoadList.slice(0, 3).map((l) => (
        <Row key={l.id} to={`/loads/${l.id}`} tile={initials(l.origin)} title={laneLabel(l.origin, l.destination)} sub={`${l.bid_count} bid${l.bid_count === 1 ? "" : "s"}`} right={<StatusLabel status={l.status} />} />
      )) }
    : { title: "Open loads", viewAll: "/loads", rows: loads.slice(0, 3).map((l) => (
        <Row key={l.id} to={`/loads/${l.id}`} tile={initials(l.origin ?? "?")} title={laneLabel(l.origin ?? "—", l.destination ?? "—")} sub={undefined} />
      )) };

  // First-run checklist — derived from live data (role-aware final step).
  const firstAction: OnboardingStep = isHiring
    ? { key: "act", label: "Post your first job", done: postings.length > 0, href: "/jobs/new", cta: "Post your first job" }
    : isPoster
    ? { key: "act", label: "Post your first shipment", done: myLoadList.length > 0, href: "/loads/new", cta: "Post your first shipment" }
    : isDriver
    ? { key: "act", label: "Apply to your first job", done: apps.length > 0, href: "/jobs", cta: "Browse jobs" }
    : isBidder
    ? { key: "act", label: "Place your first bid", done: bids.length > 0, href: "/loads", cta: "Browse loads" }
    : { key: "act", label: "Apply to your first job", done: apps.length > 0, href: "/jobs", cta: "Browse jobs" };
  const onboardingSteps: OnboardingStep[] = [
    { key: "profile", label: "Create your profile", done: profiles.length > 0, href: "/profiles/new", cta: "Create" },
    { key: "details", label: "Add a photo and headline", hint: "Stand out to the network.", done: Boolean(active?.image_url || active?.headline), href: active ? `/profiles/${active.id}/edit` : "/profiles", cta: "Edit profile" },
    { key: "verify", label: "Verify your identity and credentials", hint: "Unlocks posting, applying, and bidding.", done: verified, href: "/verify", cta: "Verify" },
    firstAction,
  ];
  const onboardingComplete = onboardingSteps.every((s) => s.done);
  const showOnboarding = !onboardingComplete && !obDismissed;

  return (
    <div className="grid gap-6">
      <PageHeader
        title={`Welcome back, ${name}`}
        subtitle="Here's what's happening across your marketplace."
        actions={
          <>
            {isHiring && <Link to="/jobs/new"><Button>Post a job</Button></Link>}
            {isPoster && <Link to="/loads/new"><Button variant={isHiring ? "ghost" : "amber"}>Post a shipment</Button></Link>}
          </>
        }
      />

      <p className="-mt-3 font-mono text-xs text-muted">
        Acting as {type ? PROFILE_TYPE_LABELS[type] : "—"}
        {active?.display_name ? ` · ${active.display_name}` : ""}
      </p>

      {/* First-run checklist (new members); the verified banner takes over once set up. */}
      {showOnboarding && <OnboardingChecklist steps={onboardingSteps} onDismiss={dismissOnboarding} />}

      {/* Verified banner */}
      {!showOnboarding && (
      <div className={`flex flex-wrap items-center gap-4 rounded-card border p-4 ${verified ? "border-verified/30 bg-verified-bg/30" : "border-accent/30 bg-accent/5"}`}>
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${verified ? "text-verified" : "text-accent-strong"}`}><Icon name="verify" className="h-7 w-7" /></span>
        <div className="min-w-0 flex-1">
          <p className={`font-mono text-sm font-bold ${verified ? "text-verified" : "text-accent-strong"}`}>{verified ? "VERIFIED" : "GET VERIFIED"}</p>
          <p className="text-sm text-muted">{verified ? "Your account is verified. You can apply, connect, post, and bid." : "Verify your identity and credentials to unlock posting, applying, and bidding."}</p>
        </div>
        <Link to={verified ? (active ? `/profiles/${active.id}/edit` : "/profiles") : "/verify"} className="font-mono text-xs font-bold text-accent-strong">
          {verified ? "Complete your profile →" : "Get verified →"}
        </Link>
      </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile icon="jobs" label="Open jobs" value={jobs.length || "—"} tone="accent" to="/jobs" />
        <StatTile icon="loads" label="Open loads" value={loads.length || "—"} tone="info" to="/loads" />
        {isHiring && <StatTile icon="folder" label="My postings" value={postings.length} tone="verified" to="/jobs/mine" />}
        {isPoster && <StatTile icon="folder" label="My loads" value={myLoadList.length} tone="verified" to="/loads/mine" />}
        {isDriver && <StatTile icon="applications" label="My applications" value={apps.length} tone="verified" to="/applications" />}
        {isBidder && !isPoster && <StatTile icon="bids" label="My bids" value={bids.length} tone="accent" to="/bids" />}
      </div>

      {/* Content sections */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title={recommended.title} action={<ViewAll to={recommended.viewAll} />}>
          {recommended.rows.length ? <div className="grid">{recommended.rows}</div> : <p className="font-mono text-sm text-muted">Nothing here yet.</p>}
        </Panel>

        <Panel title={activity.title} action={<ViewAll to={activity.viewAll} />}>
          {activity.rows.length ? <div className="grid">{activity.rows}</div> : <p className="font-mono text-sm text-muted">Nothing here yet.</p>}
        </Panel>

        <Panel title="Messages" action={<ViewAll to="/messages" />}>
          {convos.length ? (
            <div className="grid">
              {convos.slice(0, 3).map((c) => (
                <Row key={c.id} to={`/messages/${c.id}`} tile={initials(c.other_name)} title={c.other_name} sub={c.last_message ?? undefined} right={<span className="font-mono text-[10px] text-muted">{ago(c.last_message_at)}</span>} />
              ))}
            </div>
          ) : <p className="font-mono text-sm text-muted">No conversations yet.</p>}
        </Panel>

        <Panel title="Saved jobs" action={<ViewAll to="/jobs" />}>
          {savedJobs.length ? (
            <div className="grid">
              {savedJobs.slice(0, 3).map((j) => (
                <Row key={j.id} to={`/jobs/${j.id}`} tile={initials(j.poster_name ?? j.title)} title={j.title} sub={j.location ?? undefined} right={<span className="font-mono text-sm font-bold text-verified">{payLabel(j.pay_min, j.pay_max, j.pay_type) ?? "—"}</span>} />
              ))}
            </div>
          ) : <p className="font-mono text-sm text-muted">Save jobs from the board to see them here.</p>}
        </Panel>
      </div>
    </div>
  );
}
